import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  alpha,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Cancel as CancelIcon,
  Replay as RetryIcon,
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  OpenInNew as OpenIcon,
  Science as ExploreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavBack } from '../utils/navBack';
import {
  useBomExplorationJob,
  useStartBomExploration,
  useCancelBomExploration,
  useRetryFailedExploration,
  useRerunSelectedExploration,
} from '../hooks/useBomMaterialExploration';
import type { BomExplorationJob, BomExplorationItemStatus } from '../types';

interface Props {
  bomId: string;
  onViewPart?: (partId: string, tab?: number) => void;
}

const statusConfig: Record<
  string,
  { color: string; bgColor: string; icon: React.ReactNode; label: string }
> = {
  PENDING: {
    color: '#64748b',
    bgColor: '#f1f5f9',
    icon: <PendingIcon sx={{ fontSize: 16, color: '#64748b' }} />,
    label: 'Pending',
  },
  IN_PROGRESS: {
    color: '#d97706',
    bgColor: '#fffbeb',
    icon: <CircularProgress size={14} thickness={5} sx={{ color: '#d97706' }} />,
    label: 'Exploring',
  },
  COMPLETED: {
    color: '#059669',
    bgColor: '#ecfdf5',
    icon: <DoneIcon sx={{ fontSize: 16, color: '#059669' }} />,
    label: 'Complete',
  },
  FAILED: {
    color: '#dc2626',
    bgColor: '#fef2f2',
    icon: <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />,
    label: 'Failed',
  },
  SKIPPED: {
    color: '#94a3b8',
    bgColor: '#f8fafc',
    icon: <PendingIcon sx={{ fontSize: 16, color: '#94a3b8' }} />,
    label: 'Skipped',
  },
};

const BomMaterialExplorationPanel: React.FC<Props> = ({ bomId, onViewPart }) => {
  const navigate = useNavigate();
  const { navigateWithBack } = useNavBack();
  const [concurrency, setConcurrency] = useState(3);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: job, isLoading: jobLoading } = useBomExplorationJob(bomId);
  const startMutation = useStartBomExploration(bomId);
  const cancelMutation = useCancelBomExploration(bomId);
  const retryMutation = useRetryFailedExploration(bomId);
  const rerunMutation = useRerunSelectedExploration(bomId);

  const isRunning = job?.status === 'IN_PROGRESS' || job?.status === 'PENDING';
  const progress = job && job.totalItems > 0
    ? Math.round(((job.completedCount + job.failedCount + job.skippedCount) / job.totalItems) * 100)
    : 0;

  const handleStart = async () => {
    try {
      await startMutation.mutateAsync({ concurrency, forceRefresh });
      setSnackbar({ message: 'BOM exploration started', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ message: err.response?.data?.message || err.message, severity: 'error' });
    }
  };

  const handleCancel = async () => {
    if (!job) return;
    try {
      await cancelMutation.mutateAsync(job._id);
      setSnackbar({ message: 'Exploration cancelled', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ message: err.message, severity: 'error' });
    }
  };

  const handleRetry = async () => {
    if (!job) return;
    try {
      const result = await retryMutation.mutateAsync(job._id);
      setSnackbar({
        message: `Retrying ${result.retriedCount} failed items`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({ message: err.message, severity: 'error' });
    }
  };

  const handleToggleItem = (lineItemId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(lineItemId)) {
        next.delete(lineItemId);
      } else {
        next.add(lineItemId);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (!job) return;
    if (selectedItems.size === job.items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(job.items.map((i) => i.lineItemId)));
    }
  };

  const handleRerunSelected = async () => {
    if (!job || selectedItems.size === 0) return;
    try {
      const result = await rerunMutation.mutateAsync({
        jobId: job._id,
        lineItemIds: Array.from(selectedItems),
      });
      setSelectedItems(new Set());
      setSnackbar({
        message: `Re-running ${result.rerunCount} selected items`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({ message: err.message, severity: 'error' });
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '--';
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  return (
    <Box>
      {/* Controls Bar */}
      <Card
        variant="outlined"
        sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Concurrency selector */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Concurrency</InputLabel>
              <Select
                value={concurrency}
                label="Concurrency"
                onChange={(e) => setConcurrency(Number(e.target.value))}
                disabled={isRunning}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n} parallel
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Force refresh toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={forceRefresh}
                  onChange={(e) => setForceRefresh(e.target.checked)}
                  size="small"
                  disabled={isRunning}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Force refresh (bypass cache)
                </Typography>
              }
              sx={{ mr: 1 }}
            />

            {/* Action buttons */}
            {!isRunning && (
              <Button
                variant="contained"
                startIcon={<StartIcon />}
                onClick={handleStart}
                disabled={startMutation.isPending}
                sx={{
                  bgcolor: '#0f172a',
                  '&:hover': { bgcolor: '#1e293b' },
                  textTransform: 'none',
                }}
              >
                {startMutation.isPending ? 'Starting...' : 'Start Exploration'}
              </Button>
            )}

            {isRunning && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            )}

            {job && job.failedCount > 0 && !isRunning && (
              <Button
                variant="outlined"
                startIcon={<RetryIcon />}
                onClick={handleRetry}
                disabled={retryMutation.isPending}
                sx={{ textTransform: 'none' }}
              >
                Retry Failed ({job.failedCount})
              </Button>
            )}

            {job && selectedItems.size > 0 && !isRunning && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RetryIcon />}
                onClick={handleRerunSelected}
                disabled={rerunMutation.isPending}
                sx={{ textTransform: 'none' }}
              >
                {rerunMutation.isPending ? 'Re-running...' : `Re-run Selected (${selectedItems.size})`}
              </Button>
            )}

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Stats chips */}
            {job && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`Total: ${job.totalItems}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Done: ${job.completedCount}`}
                  size="small"
                  sx={{ bgcolor: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0' }}
                />
                {job.failedCount > 0 && (
                  <Chip
                    label={`Failed: ${job.failedCount}`}
                    size="small"
                    sx={{ bgcolor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                  />
                )}
                {(job.totalItems - job.completedCount - job.failedCount - job.skippedCount) > 0 && isRunning && (
                  <Chip
                    label={`Remaining: ${job.totalItems - job.completedCount - job.failedCount - job.skippedCount}`}
                    size="small"
                    sx={{ bgcolor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Progress bar */}
          {isRunning && job && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Processing... {job.completedCount + job.failedCount}/{job.totalItems}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#f1f5f9',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#0f172a',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          )}

          {/* Job metadata */}
          {job && !isRunning && job.status !== 'PENDING' && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip
                label={job.status}
                size="small"
                sx={{
                  bgcolor:
                    job.status === 'COMPLETED' ? '#ecfdf5' :
                    job.status === 'FAILED' ? '#fef2f2' :
                    job.status === 'CANCELLED' ? '#fffbeb' : '#f1f5f9',
                  color:
                    job.status === 'COMPLETED' ? '#059669' :
                    job.status === 'FAILED' ? '#dc2626' :
                    job.status === 'CANCELLED' ? '#d97706' : '#64748b',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Duration: {formatDuration(job.durationMs)}
              </Typography>
              {job.completedAt && (
                <Typography variant="caption" color="text.secondary">
                  Completed: {new Date(job.completedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Empty state */}
      {!job && !jobLoading && (
        <Card
          variant="outlined"
          sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <ExploreIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No exploration started yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Start Exploration" to run deep material sourcing research on every line item in this BOM.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {jobLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Items table */}
      {job && job.items.length > 0 && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell padding="checkbox" sx={{ width: 42 }}>
                  <Checkbox
                    size="small"
                    checked={job.items.length > 0 && selectedItems.size === job.items.length}
                    indeterminate={selectedItems.size > 0 && selectedItems.size < job.items.length}
                    onChange={handleToggleAll}
                    disabled={isRunning}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>
                  Part Number
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>
                  Description
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>
                  Suppliers
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>
                  Confidence
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b' }}>
                  Duration
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#64748b', width: 40 }}>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {job.items.map((item) => {
                const cfg = statusConfig[item.status] || statusConfig.PENDING;
                return (
                  <TableRow
                    key={item.lineItemId}
                    onClick={() => {
                      if (item.partId) {
                        if (onViewPart) {
                          onViewPart(item.partId, 5);
                        } else {
                          navigateWithBack(`/parts/${item.partId}?tab=exploration`);
                        }
                      }
                    }}
                    sx={{
                      '&:hover': { bgcolor: alpha('#0f172a', 0.04) },
                      bgcolor: item.status === 'IN_PROGRESS' ? alpha('#d97706', 0.04) : 'transparent',
                      cursor: item.partId ? 'pointer' : 'default',
                    }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selectedItems.has(item.lineItemId)}
                        onChange={() => handleToggleItem(item.lineItemId)}
                        disabled={isRunning}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={cfg.icon as React.ReactElement}
                        label={cfg.label}
                        size="small"
                        sx={{
                          bgcolor: cfg.bgColor,
                          color: cfg.color,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 24,
                          '& .MuiChip-icon': { ml: 0.5 },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                        {item.partNumber || '--'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.description || '--'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {item.status === 'COMPLETED' ? (
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: '#059669' }}>
                          {item.suppliersFound || 0}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>--</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {item.status === 'COMPLETED' && item.confidence != null ? (
                        <Chip
                          label={`${Math.round(item.confidence * 100)}%`}
                          size="small"
                          sx={{
                            bgcolor: item.confidence >= 0.6 ? '#ecfdf5' : item.confidence >= 0.3 ? '#fffbeb' : '#fef2f2',
                            color: item.confidence >= 0.6 ? '#059669' : item.confidence >= 0.3 ? '#d97706' : '#dc2626',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                          }}
                        />
                      ) : item.status === 'FAILED' ? (
                        <Tooltip title={item.error || 'Unknown error'} arrow>
                          <ErrorIcon sx={{ fontSize: 16, color: '#dc2626', cursor: 'help' }} />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>--</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {formatDuration(item.durationMs)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {item.status === 'COMPLETED' && item.partId && (
                        <Tooltip title="View exploration results" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (onViewPart) {
                                onViewPart(item.partId!, 5);
                              } else {
                                navigateWithBack(`/parts/${item.partId}?tab=exploration`);
                              }
                            }}
                          >
                            <OpenIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar */}
      {snackbar && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default BomMaterialExplorationPanel;
